import { useState } from 'react';
import _noop from 'lodash/noop';
import * as yup from 'yup';
import {
	RenderConfig,
	DateRangeInput,
	KEY_VALUE_LAYOUT,
	FILTER_LAYOUT,
	MIN_DATE,
	MAX_DATE,
	MIN_DATE_OBJECT,
	MAX_DATE_OBJECT,
	KeyConfig
} from 'commons-ui';

import usePermissionV2 from 'commons-ui/src/permission/usePermission';
import { ACTION, PAGE_NAMES, USER_PROFILE_RESOURCES } from 'constant';
import useService from './useService';
import { VIEW_FIELD_KEYS, DEFAULT_PORTFOLIO_DATE_RANGE } from './constants';

const useUserStats = () => {
	const [opponentsDetailsIsCollapsed, setOpponentsDetailsIsCollapsed] = useState<boolean>(true);
	const { getUserActivity, getUserPortfolio, getUserNetPortfolio, getUserHeartBeatStatus, getUserCallingSlots } = useService();
	const { hasPermission: hasPermissionV2 } = usePermissionV2();

	const keyConfig: KeyConfig = {
		childConfig: [
			{
				name: VIEW_FIELD_KEYS.HEARTBEAT_STATUS,
				label: 'User Online Status',
				initiallyCollapsed: true,
				fetchData: getUserHeartBeatStatus,
				isHeavyFetch: false,
				isHidden: !hasPermissionV2({
					configPageName: PAGE_NAMES.userProfile,
					configResourceName: USER_PROFILE_RESOURCES.userStats,
					configActionName: ACTION.getUsersHeartBeatDetailsBulk
				}),
				childConfig: [
					{
						name: VIEW_FIELD_KEYS.ONLINE_STATUS,
						label: 'Status'
					},
					{
						name: VIEW_FIELD_KEYS.LAST_SEEN,
						label: 'Last Seen'
					}
				]
			},
			{
				name: VIEW_FIELD_KEYS.CALLING_SLOTS,
				label: 'User Calling Slots',
				initiallyCollapsed: true,
				fetchData: getUserCallingSlots,
				isHeavyFetch: true,
				isHidden: !hasPermissionV2({
					configPageName: PAGE_NAMES.userProfile,
					configResourceName: USER_PROFILE_RESOURCES.userStats,
					configActionName: ACTION.getUserCallingSlots
				}),
				childConfig: [
					{
						name: VIEW_FIELD_KEYS.RECOMMENDED_CALLING_SLOTS,
						label: 'Recommended Calling Slots'
					},
					{
						name: VIEW_FIELD_KEYS.BEST_SLOT,
						label: 'Best Slot'
					},
					{
						name: VIEW_FIELD_KEYS.SECOND_BEST_SLOT,
						label: 'Second Best Slot'
					},
					{
						name: VIEW_FIELD_KEYS.THIRD_BEST_SLOT,
						label: 'Third Best Slot'
					},
					{
						name: VIEW_FIELD_KEYS.BEST_SLOT_ALL_HOURS,
						label: 'Best Slot (All Hours)'
					},
					{
						name: VIEW_FIELD_KEYS.SECOND_BEST_SLOT_ALL_HOURS,
						label: 'Second Best Slot (All Hours)'
					},
					{
						name: VIEW_FIELD_KEYS.THIRD_BEST_SLOT_ALL_HOURS,
						label: 'Third Best Slot (All Hours)'
					}
				]
			},
			{
				name: VIEW_FIELD_KEYS.USER_ACTIVITY,
				label: 'User Tracking Activity',
				initiallyCollapsed: true,
				fetchData: getUserActivity,
				isHeavyFetch: true,
				isHidden: !hasPermissionV2({
					configPageName: PAGE_NAMES.userProfile,
					configResourceName: USER_PROFILE_RESOURCES.userStats,
					configActionName: ACTION.getUserSignUpAndLastActivityV2
				}),
				childConfig: [
					{
						name: VIEW_FIELD_KEYS.SIGN_UP,
						label: 'Sign Up',
						childConfig: [
							{
								name: VIEW_FIELD_KEYS.TIMESTAMP,
								label: 'Timestamp'
							},
							{
								name: VIEW_FIELD_KEYS.LOCATION,
								label: 'Location'
							},
							{
								name: VIEW_FIELD_KEYS.IP_ADDRESS,
								label: 'IP Address'
							}
						]
					},
					{
						name: VIEW_FIELD_KEYS.LAST_ACTIVITY,
						label: 'Last Activity',
						childConfig: [
							{
								name: VIEW_FIELD_KEYS.LA_LOGIN_TIMESTAMP,
								label: 'Login Timestamp'
							},
							{
								name: VIEW_FIELD_KEYS.LA_LOCATION,
								label: 'Location'
							},
							{
								name: VIEW_FIELD_KEYS.LA_IP_ADDRESS,
								label: 'IP Address'
							}
						]
					}
				]
			},
			{
				name: VIEW_FIELD_KEYS.PORTFOLIO,
				label: 'User Gameplay Stats',
				initiallyCollapsed: true,
				isHidden: !hasPermissionV2({
					configPageName: PAGE_NAMES.userProfile,
					configResourceName: USER_PROFILE_RESOURCES.userStats,
					configActionName: ACTION.getUserPortfolio
				}),
				fetchData: getUserPortfolio,
				isHeavyFetch: true,
				fetchOnlyOnFilterChange: true,
				filterConfig: {
					layoutPosition: FILTER_LAYOUT.top,
					componentsRenderConfig: [
						new RenderConfig().setName('fromToDate').setRenderer(DateRangeInput).setRenderProps({
							placeholder: 'from-To date',
							noWrapper: true
						})
					],
					filterDefaults: {
						fromToDate: DEFAULT_PORTFOLIO_DATE_RANGE
					},
					validationSchema: yup.object().shape({
						fromToDate: yup
							.array()
							.test({
								message: 'Incomplete date range',
								test: (value: any) => value.length === 2
							})
							.test({
								message: `"from" date must be in a range of ${MIN_DATE} to ${MAX_DATE}`,
								test: (value: any) => value[0] >= MIN_DATE_OBJECT && value[0] <= MAX_DATE_OBJECT
							})
							.test({
								message: `"To" date must be in a range of ${MIN_DATE} to ${MAX_DATE}`,
								test: (value: any) => value[1] >= MIN_DATE_OBJECT && value[1] <= MAX_DATE_OBJECT
							})
					})
				},
				childConfig: [
					{
						name: VIEW_FIELD_KEYS.TOTAL_GAMEPLAYS,
						label: 'Total Gameplays'
					},
					{
						name: VIEW_FIELD_KEYS.TOTAL_TICKETS_RAISED,
						label: 'Total Tickets Raised'
					},
					{
						name: VIEW_FIELD_KEYS.TOTAL_REFERRALS,
						label: 'Total Referrals'
					},
					{
						name: VIEW_FIELD_KEYS.NUMBER_OF_DEPOSITS,
						label: 'Number of Deposits'
					},
					{
						name: VIEW_FIELD_KEYS.LIFETIME_DEPOSITS,
						label: 'Lifetime Deposits (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.LIFETIME_WINNINGS,
						label: 'Lifetime Winnings (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.LIFETIME_RAKE,
						label: 'Lifetime Rake (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.LIFETIME_GAMEPLAY_LOSSES,
						label: 'Lifetime Gameplay Losses (₹)'
					}
				]
			},
			{
				name: VIEW_FIELD_KEYS.NET_PORTFOLIO,
				label: 'User Lifetime Txn Details',
				initiallyCollapsed: true,
				isHidden: !hasPermissionV2({
					configPageName: PAGE_NAMES.userProfile,
					configResourceName: USER_PROFILE_RESOURCES.userStats,
					configActionName: ACTION.getUserNetPortfolio
				}),
				fetchData: getUserNetPortfolio,
				isHeavyFetch: true,
				childConfig: [
					{
						name: VIEW_FIELD_KEYS.LIFETIME_DEPOSITS_PORTFOLIO,
						label: 'Lifetime Deposits (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.LIFETIME_WITHDRAWALS_PORTFOLIO,
						label: 'Lifetime Withdrawals (₹)'
					},
					{
						name: VIEW_FIELD_KEYS.LIFETIME_NET_PORTFOLIO,
						label: 'Lifetime Net Portfolio (₹) [Deposits - Withdrawals]'
					}
				]
			}
		],
		name: VIEW_FIELD_KEYS.USER_STATS_INFO,
		fetchData: _noop
	};

	return {
		keyConfig,
		keyValueLayout: KEY_VALUE_LAYOUT.HORIZONTAL,
		opponentsDetailsIsCollapsed,
		setOpponentsDetailsIsCollapsed
	};
};

export default useUserStats;
